import React, { useState, useEffect } from 'react';
import { Button, Grid, Stack, TextField, Typography, Card, styled, CardContent, Divider } from '@mui/material';
import moment from 'moment';

import { LoadingButton } from '@mui/lab';
import Page from '../../components/Page';
import conObj from '../../utils/connection-assistant';
import Scrollbar from '../../components/Scrollbar';
import useTable, { getComparator } from '../../hooks/useTable';
import { TableSkeleton } from '../../components/table';
import useLocales from '../../hooks/useLocales';
import { useSelector } from '../../redux/store';
import CustomDateRangePicker from '../../components/CustomDateRangePicker';
import SvgIconStyle from '../../components/SvgIconStyle';
import Image from '../../components/Image';
import { unformatNumber } from '../../utils/formatNumber';

const DEFAULT_FILTERS = {
  dateTo: null,
  dateFrom: null,
  gameCateg: '',
};

const DEFAULT_FILTERS_ERRORS = {
  dateTo: null,
  dateFrom: null,
  gameCateg: '',
};

const InputBox = styled(Stack)(({ theme }) => ({
  background: 'rgb(8, 15, 28)',
  border: '1px solid rgb(32, 46, 65)',
  padding: '.375rem .75rem',
  borderRadius: '.25rem',
  cursor: 'pointer',
  alignItems: 'center',
}));

const DateText = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: '#fff',
  fontSize: '.75rem',
}));

const StyledCard = styled(Card)({
  marginBottom: '10px',
  borderRadius: 8,
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  backgroundColor: '#2e2e2e',
  '& > :last-child': {
    padding: '10px 20px',
  },
});

const StyledCardContent = styled(CardContent)({
  padding: '10px 20px',
  '&:last-child': {
    padding: '10px 20px',
  },
});

const StyledCardTag = styled('div')({
  borderRadius: '8px',
  backgroundColor: '#00101d',
  fontSize: '3px',
  padding: '5px 15px',
});

const StyledCardSmallText = styled('p')({
  margin: 0,
  padding: 0,
  color: 'rgba(255, 255, 255, 0.7)',
  fontSize: '10px',
});

const EmptyContainer = styled(Stack)({
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  backgroundColor: '#383649',
  color: 'rgba(255, 255, 255, 0.4)',
});

const StyledButton = styled(LoadingButton)({
  background: 'linear-gradient(165deg, rgb(0, 199, 199) -17.16%, rgb(19, 38, 216) 91.36%)',
  boxShadow: 'rgba(0, 0, 0, 0.25) 0px 4px 4px 0px',
  color: '#fff',
  fontWeight: 500,
});

const StyledButtonNav = styled(Button)({
  borderColor: '#FCD509',
  backgroundColor: '#FCD509',
  '&:hover': {
    borderColor: '#FCD509',
    backgroundColor: 'rgba(255, 255, 0, 0.1)',
  },
  '&:focus': {
    borderColor: '#FCD509',
    backgroundColor: 'rgba(255, 255, 0, 0.1)',
    outline: 'none',
  },
});

const GameHistory = () => {
  const { translate } = useLocales();

  const { memberBalance } = useSelector((x) => x.lookup);

  const [gameCategories, setGameCategories] = useState([]);

  const [tableData, setTableData] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const [inputFilters, setInputFilters] = useState(DEFAULT_FILTERS);

  const [searchFilters, setSearchFilters] = useState(DEFAULT_FILTERS);

  const [filterName, setFilterName] = useState('');

  const [errors, setErrors] = useState(DEFAULT_FILTERS_ERRORS);

  const [amountInThousandths, setAmountInThousandths] = useState(false);

  const [page, setPage] = useState(1);

  const [openDateRangePicker, setOpenDateRangePicker] = useState(false);

  const [dateRange, setDateRange] = useState(null);

  const pageLimit = 5;

  const {
    dense,
    // page,
    order,
    orderBy,
    rowsPerPage,
    // setPage,
    //
    // selected,
    // setSelected,
    // onSelectRow,
    // onSelectAllRows,
    //
    onSort,
    // onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable();

  useEffect(() => {
    getGameCategories();
  }, []);

  useEffect(() => {
    getHistories();
  }, [page, inputFilters]);

  useEffect(() => {
    if (dateRange) {
      if (dateRange?.start) {
        setInputFilters((prevState) => ({ ...prevState, dateFrom: dateRange?.start }));
      }
      if (dateRange?.end) {
        setInputFilters((prevState) => ({ ...prevState, dateTo: dateRange?.end }));
      }
    }
  }, [dateRange]);

  useEffect(() => {
    if (memberBalance?.currencyCode.toUpperCase() === 'MMK' || memberBalance?.currencyCode.toUpperCase() === 'VND') {
      setAmountInThousandths(true);
    }
  }, [memberBalance]);

  const handleFilterChange = (name, value) => {
    setErrors(DEFAULT_FILTERS_ERRORS);
    setInputFilters((prev) => ({ ...prev, [name]: value }));
  };

  const getHistories = () => {
    setIsLoading(true);

    const requestData = {
      'named-date_from': inputFilters?.dateFrom ? moment(inputFilters?.dateFrom).format('YYYY-MM-DD') : '',
      'named-date_to': inputFilters?.dateTo ? moment(inputFilters?.dateTo).format('YYYY-MM-DD') : '',
      'named-category': inputFilters?.gameCateg ? inputFilters.gameCateg : '',
    };

    conObj
      .post(`games/histories.json?page=${page}&limit=${pageLimit}`, requestData)
      .then((res) => {
        if (res.status === 200) {
          setTableData(res.data?.data?.data);
        }
      })
      .catch((err) => {
        setTableData([]);
        console.log(`Server Error: ${err.message}`);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getGameCategories = () => {
    conObj
      .post(`games/categories.json`)
      .then((res) => {
        if (res.status === 200) {
          setGameCategories(res.data?.data?.data);
        }
      })
      .catch((err) => {
        console.log(`Server Error: ${err.message}`);
      });
  };

  const dataFiltered = applySortFilter({
    tableData,
    comparator: getComparator(order, orderBy),
    filterName,
  });

  const denseHeight = dense ? 60 : 80;

  const isNotFound = (!dataFiltered.length && !!filterName) || (!isLoading && !dataFiltered.length);

  return (
    <Page title="Bet History" sx={{ p: { xs: 1 } }}>
      <Scrollbar>
        <Stack
          direction={{ xl: 'row', lg: 'row', md: 'row', sm: 'row', xs: 'column' }}
          spacing={1}
          sx={{ my: '10px', alignItems: 'start' }}
        >
          <Grid container spacing={'0.5rem'}>
            <Grid item xs={6}>
              <InputBox
                onClick={() => {
                  if (isLoading) setOpenDateRangePicker(true);
                }}
                direction="row"
              >
                <Stack sx={{ flex: 1 }}>
                  <DateText>{translate('date_from')}</DateText>
                  <DateText sx={{ fontSize: '.875rem' }}>
                    {inputFilters?.dateFrom ? moment(inputFilters?.dateFrom).format('DD/MM/YYYY') : translate('from')}
                  </DateText>
                </Stack>
                <Image src={require('../../assets/history/schedule.png')} sx={{ height: '26px', width: '26px' }} />
              </InputBox>
            </Grid>
            <Grid item xs={6}>
              <InputBox
                onClick={() => {
                  if (isLoading) setOpenDateRangePicker(true);
                }}
                direction="row"
              >
                <Stack sx={{ flex: 1 }}>
                  <DateText sx={{ fontWeight: 500, color: '#fff', fontSize: '.75rem' }}>
                    {translate('date_to')}
                  </DateText>
                  <DateText sx={{ fontSize: '.875rem' }}>
                    {inputFilters?.dateTo ? moment(inputFilters?.dateTo).format('DD/MM/YYYY') : translate('to')}
                  </DateText>
                </Stack>
                <Image src={require('../../assets/history/schedule.png')} sx={{ height: '26px', width: '26px' }} />
              </InputBox>
            </Grid>
          </Grid>
          <Stack direction="row" spacing={1}>
            <InputBox>
              <select
                size="small"
                label={translate('game_category')}
                value={inputFilters.gameCateg}
                onChange={(e) => handleFilterChange('gameCateg', e.target.value)}
                select
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                disabled={isLoading}
                style={{
                  background: 'transparent',
                  color: '#fff',
                  fontWeight: 500,
                  fontSize: '.75rem',
                  border: 'none',
                  height: '100%',
                }}
              >
                <option value="" />
                {gameCategories.map((gc) => (
                  <option key={gc?.id} value={gc?.id}>
                    {gc?.name}
                  </option>
                ))}
              </select>
            </InputBox>
            <StyledButton
              variant="outlined"
              disabled={isLoading}
              onClick={() => {
                setInputFilters(DEFAULT_FILTERS);
                setSearchFilters(DEFAULT_FILTERS);
                setErrors(DEFAULT_FILTERS_ERRORS);
                setPage(1);
              }}
            >
              {translate('clear')}
            </StyledButton>
            <StyledButton
              variant="outlined"
              loading={isLoading}
              disabled={isLoading}
              onClick={() => {
                setSearchFilters(inputFilters);
                setPage(1);
              }}
            >
              {translate('search')}
            </StyledButton>
          </Stack>
        </Stack>
        {/* NEW DESIGN START */}
        {dataFiltered.length > 0 ? (
          dataFiltered.map((row, index) =>
            row ? (
              <StyledCard key={row.id}>
                <StyledCardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems={'center'} spacing={2}>
                    <Typography variant="h6" gutterBottom>
                      {row.title}
                    </Typography>
                    <Typography
                      variant="h6"
                      gutterBottom
                      style={{ color: unformatNumber(row.win?.value) < 0 ? '#ff2400' : '#00ff00' }}
                    >
                      {row.win
                        ? `${process.env.REACT_APP_CURRENCY_CODE} ${
                            unformatNumber(row.win?.value) < 0 ? row.win?.value : `+${row.win?.value}`
                          }`
                        : '-'}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} justifyContent={'space-between'} alignItems={'center'}>
                    <Stack direction="row" spacing={1}>
                      <StyledCardTag>
                        <Typography variant="caption" mt={2}>
                          {row.company_name}
                        </Typography>
                      </StyledCardTag>
                      <StyledCardTag>
                        <Typography variant="caption" mt={2}>
                          {row.Category.name}
                        </Typography>
                      </StyledCardTag>
                    </Stack>
                    <Typography variant="caption" gutterBottom style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {row.date}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} justifyContent={'space-between'} mt={2}>
                    <Stack direction="column" alignItems={'center'}>
                      <StyledCardSmallText variant="caption">
                        {translate('bet_amount').toUpperCase()}
                      </StyledCardSmallText>
                      <Typography variant="body2">
                        {row.bet ? `${process.env.REACT_APP_CURRENCY_CODE} ${parseFloat(row.bet?.value)}` : '-'}
                      </Typography>
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                    <Stack direction="column" alignItems={'center'}>
                      <StyledCardSmallText variant="caption">{translate('rebate').toUpperCase()}</StyledCardSmallText>
                      <Typography variant="body2">
                        {row.bet ? `${process.env.REACT_APP_CURRENCY_CODE} ${parseFloat(row.rebate?.value)}` : '-'}
                      </Typography>
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                    <Stack direction="column" alignItems={'center'}>
                      <StyledCardSmallText variant="caption">{translate('turnover').toUpperCase()}</StyledCardSmallText>
                      <Typography variant="body2">
                        {row.bet ? `${process.env.REACT_APP_CURRENCY_CODE} ${parseFloat(row.turnover?.value)}` : '-'}
                      </Typography>
                    </Stack>
                  </Stack>
                </StyledCardContent>
              </StyledCard>
            ) : (
              !isNotFound && <TableSkeleton key={index} sx={{ height: denseHeight }} />
            )
          )
        ) : (
          <EmptyContainer direction={'column'} alignItems={'center'} py={5}>
            <SvgIconStyle
              src={`/assets/icons/others/folder.svg`}
              sx={{ height: 90, width: 90, color: 'rgba(255, 255, 255, 0.2)' }}
            />
            <Typography variant="body2" sx={{ padding: 0 }}>
              {translate('nothing_to_show')}
            </Typography>
          </EmptyContainer>
        )}

        <Stack direction="row" spacing={1} my={2} justifyContent={'end'} alignItems={'center'}>
          <Stack direction="row" spacing={1} alignItems={'center'}>
            <StyledButtonNav
              variant="contained"
              disabled={page === 1}
              onClick={() => {
                if (page > 1) {
                  setPage(page - 1);
                }
              }}
              sx={{ minWidth: 0, paddingX: 1 }}
            >
              <SvgIconStyle src={`/assets/icons/pagination/arrow_back.svg`} sx={{ height: 20 }} />
            </StyledButtonNav>
            <Typography sx={{ paddingX: 2, paddingY: 0, margin: 0 }}>{page}</Typography>
            <StyledButtonNav
              variant="contained"
              disabled={tableData.length < pageLimit}
              onClick={() => {
                setPage(page + 1);
              }}
              sx={{ minWidth: 0, paddingX: 1 }}
            >
              <SvgIconStyle src={`/assets/icons/pagination/arrow_next.svg`} sx={{ height: 20 }} />
            </StyledButtonNav>
          </Stack>
        </Stack>
      </Scrollbar>
      {/* MODAL FOR DATE */}
      {openDateRangePicker && (
        <CustomDateRangePicker
          open={openDateRangePicker}
          setOpen={setOpenDateRangePicker}
          setDateRange={setDateRange}
          dateRange={dateRange}
          inputProps={{
            width: '100%',
          }}
        />
      )}
    </Page>
  );
};

function applySortFilter({ tableData, comparator, filterName }) {
  const stabilizedThis = tableData.map((el, index) => [el, index]);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  tableData = stabilizedThis.map((el) => el[0]);

  if (filterName) {
    tableData = tableData.filter((item) => item.name.toLowerCase().indexOf(filterName.toLowerCase()) !== -1);
  }

  return tableData;
}

export default GameHistory;
